import React, { useState, useEffect, useRef } from "react";
import { Modal, Spin, Icon, Button, message } from "antd";
import { platformApi } from "../../../../api";
import { io } from "socket.io-client";
import { ENDPOINT } from "../../../../constants";

const PymidolBookingSyncModal = ({ visible, record, onClose, onSuccess }) => {
  const [status, setStatus] = useState("idle"); // "idle" | "phase1_running" | "success" | "phase2_running" | "phase2_success" | "phase3_running" | "error"
  const [errorMsg, setErrorMsg] = useState("");
  const [enquiryNo, setEnquiryNo] = useState("");
  const [isExistingEnquiry, setIsExistingEnquiry] = useState(false);
  const [pymidolBookingNo, setPymidolBookingNo] = useState("");
  const [isExistingBooking, setIsExistingBooking] = useState(false);
  const [bookingDetails, setBookingDetails] = useState(null);
  const [countdown, setCountdown] = useState(4);
  const countdownTimerRef = useRef(null);
  const isTriggeredRef = useRef(false);

  // OTP Verification States
  const [otpValues, setOtpValues] = useState(["", "", "", ""]);
  const [otpMobileNo, setOtpMobileNo] = useState("");
  const [otpError, setOtpError] = useState("");
  const [otpSubmitting, setOtpSubmitting] = useState(false);
  const [otpSecondsLeft, setOtpSecondsLeft] = useState(180); // 3 minutes total window
  const [canResendOtp, setCanResendOtp] = useState(false);
  const [resendCooldown, setResendCooldown] = useState(60);
  const otpInputsRef = useRef([]);

  const getUserIdFromToken = () => {
    try {
      const token = localStorage.getItem("token");
      if (!token) return null;
      const base64Url = token.split(".")[1];
      const base64 = base64Url.replace(/-/g, "+").replace(/_/g, "/");
      const jsonPayload = decodeURIComponent(
        window
          .atob(base64)
          .split("")
          .map((c) => "%" + ("00" + c.charCodeAt(0).toString(16)).slice(-2))
          .join("")
      );
      return JSON.parse(jsonPayload).id;
    } catch (e) {
      return null;
    }
  };

  useEffect(() => {
    if (!visible) return;

    // Connect to backend websocket for real-time progress events
    const socket = io(ENDPOINT, {
      transports: ["websocket"],
      auth: { token: localStorage.getItem("token") },
    });

    socket.on("pymidol_booking_progress", (data) => {
      const currentUserId = getUserIdFromToken();
      if (data && (data.userId === currentUserId || !data.userId)) {
        console.log("📡 Received Booking RFR sync progress update:", data);
        if (data.status === "phase1_running") {
          setStatus("phase1_running");
        } else if (data.status === "phase2_running") {
          setStatus("phase2_running");
        } else if (data.status === "success" && data.phase !== 2) {
          if (data.enquiryNo) {
            setEnquiryNo(data.enquiryNo);
          }
          if (data.alreadyExists) {
            setIsExistingEnquiry(true);
          }
          setStatus("success");
        } else if (data.status === "phase2_success" || (data.status === "success" && data.phase === 2)) {
          if (data.bookingNo) {
            setPymidolBookingNo(data.bookingNo);
          }
          if (data.alreadyExists) {
            setIsExistingBooking(true);
          }
          setStatus("phase2_success");
        } else if (data.status === "waiting_for_otp") {
          setStatus("waiting_for_otp");
          setOtpSubmitting(false);
          setOtpValues(["", "", "", ""]);
          setOtpError("");
          if (data.mobileNo) {
            setOtpMobileNo(data.mobileNo);
          }
          if (data.resent) {
            setResendCooldown(60);
            setCanResendOtp(false);
            message.info("New OTP sent to customer mobile.");
          }
          setTimeout(() => {
            otpInputsRef.current[0]?.focus();
          }, 300);
        } else if (data.status === "otp_invalid") {
          setOtpSubmitting(false);
          setOtpValues(["", "", "", ""]);
          setOtpError(data.message || "Incorrect OTP. Please check with customer and enter again.");
          setTimeout(() => {
            otpInputsRef.current[0]?.focus();
          }, 200);
        } else if (data.status === "otp_verified") {
          setOtpSubmitting(false);
          setOtpError("");
          message.success("Customer mobile verified successfully!");
          if (data.bookingNo) {
            setPymidolBookingNo(data.bookingNo);
          }
          setStatus("phase2_running");
        } else if (data.status === "error") {
          setStatus("error");
          setErrorMsg(data.message || "Automation failed to complete.");
        }
      }
    });

    return () => {
      socket.disconnect();
    };
  }, [visible]);

  // Handle countdown timers for Phase 2 and Phase 3 transitions
  useEffect(() => {
    if (status === "success") {
      setCountdown(4);
      if (countdownTimerRef.current) {
        clearInterval(countdownTimerRef.current);
      }
      countdownTimerRef.current = setInterval(() => {
        setCountdown((prev) => {
          if (prev <= 1) {
            clearInterval(countdownTimerRef.current);
            countdownTimerRef.current = null;
            checkAndStartPhase2();
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
    } else if (status === "phase2_success") {
      setCountdown(4);
      if (countdownTimerRef.current) {
        clearInterval(countdownTimerRef.current);
      }
      countdownTimerRef.current = setInterval(() => {
        setCountdown((prev) => {
          if (prev <= 1) {
            clearInterval(countdownTimerRef.current);
            countdownTimerRef.current = null;
            checkAndStartPhase3();
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
    } else {
      if (countdownTimerRef.current) {
        clearInterval(countdownTimerRef.current);
        countdownTimerRef.current = null;
      }
    }

    return () => {
      if (countdownTimerRef.current) {
        clearInterval(countdownTimerRef.current);
        countdownTimerRef.current = null;
      }
    };
  }, [status]);

  useEffect(() => {
    if (visible && record) {
      if (!isTriggeredRef.current) {
        isTriggeredRef.current = true;
        handleStartPush();
      }
    } else {
      isTriggeredRef.current = false;
      setStatus("idle");
      setErrorMsg("");
      setEnquiryNo("");
      setIsExistingEnquiry(false);
      setPymidolBookingNo("");
      setIsExistingBooking(false);
      setBookingDetails(null);
      setCountdown(4);
      setOtpValues(["", "", "", ""]);
      setOtpError("");
      setOtpSubmitting(false);
      setOtpSecondsLeft(180);
      setCanResendOtp(false);
      setResendCooldown(60);
      if (countdownTimerRef.current) {
        clearInterval(countdownTimerRef.current);
        countdownTimerRef.current = null;
      }
    }
  }, [visible, record]);

  // Handle 3-minute overall OTP countdown and 60s resend cooldown
  useEffect(() => {
    let timer = null;
    if (status === "waiting_for_otp") {
      timer = setInterval(() => {
        setOtpSecondsLeft((prev) => (prev > 0 ? prev - 1 : 0));
        setResendCooldown((prev) => {
          if (prev <= 1) {
            setCanResendOtp(true);
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
    } else {
      setOtpSecondsLeft(180);
      setResendCooldown(60);
      setCanResendOtp(false);
      setOtpSubmitting(false);
      setOtpError("");
    }
    return () => {
      if (timer) clearInterval(timer);
    };
  }, [status]);

  const handleDigitChange = (index, value) => {
    const digit = value.replace(/\D/g, "").slice(-1);
    const newOtp = [...otpValues];
    newOtp[index] = digit;
    setOtpValues(newOtp);
    setOtpError("");

    if (digit && index < 3) {
      otpInputsRef.current[index + 1]?.focus();
    }

    if (newOtp.every((d) => d !== "") && newOtp.join("").length === 4) {
      handleAutoSubmitOtp(newOtp.join(""));
    }
  };

  const handleDigitKeyDown = (index, e) => {
    if (e.key === "Backspace") {
      if (!otpValues[index] && index > 0) {
        otpInputsRef.current[index - 1]?.focus();
      }
    } else if (e.key === "ArrowLeft" && index > 0) {
      otpInputsRef.current[index - 1]?.focus();
    } else if (e.key === "ArrowRight" && index < 3) {
      otpInputsRef.current[index + 1]?.focus();
    }
  };

  const handleOtpPaste = (e) => {
    e.preventDefault();
    const pasteData = e.clipboardData.getData("text").replace(/\D/g, "").slice(0, 4);
    if (!pasteData) return;
    const digits = pasteData.split("");
    const newOtp = ["", "", "", ""];
    digits.forEach((d, i) => {
      newOtp[i] = d;
    });
    setOtpValues(newOtp);
    setOtpError("");
    const nextIndex = Math.min(digits.length, 3);
    otpInputsRef.current[nextIndex]?.focus();

    if (newOtp.every((d) => d !== "") && newOtp.join("").length === 4) {
      handleAutoSubmitOtp(newOtp.join(""));
    }
  };

  const handleAutoSubmitOtp = (otpCode) => {
    const targetId = record?.bookingUuid || record?.id || record?.saleId;
    if (!targetId || !otpCode) return;
    setOtpSubmitting(true);
    setOtpError("");

    platformApi
      .post("/api/saleRegister/submitPymidolOtp/" + targetId, { otp: otpCode })
      .then((res) => {
        console.log("OTP submitted successfully:", res.data);
      })
      .catch((err) => {
        console.error("Error submitting OTP:", err);
        setOtpSubmitting(false);
      });
  };

  const handleResendOtp = () => {
    const targetId = record?.bookingUuid || record?.id || record?.saleId;
    if (!targetId) return;
    setCanResendOtp(false);
    setResendCooldown(60);
    setOtpValues(["", "", "", ""]);
    setOtpError("");
    otpInputsRef.current[0]?.focus();

    platformApi
      .post("/api/saleRegister/resendPymidolOtp/" + targetId)
      .then(() => {
        message.info("Requested new OTP from Pymidol.");
      })
      .catch((err) => {
        console.error("Error requesting resend OTP:", err);
      });
  };

  const checkAndStartPhase2 = () => {
    if (countdownTimerRef.current) {
      clearInterval(countdownTimerRef.current);
      countdownTimerRef.current = null;
    }

    // Check if Pymidol bookingNo already exists on this record
    const existingBookingNo = record?.bookingNo || record?.saleData?.bookingNo;
    const isInternalId = existingBookingNo && (
      existingBookingNo === record?.bookingId ||
      existingBookingNo === record?.saleId ||
      existingBookingNo === record?.id ||
      /^[A-Z]{2,4}\d{2}-\d{2}\//i.test(String(existingBookingNo).trim())
    );
    const hasValidPymidolBooking = existingBookingNo &&
      String(existingBookingNo).trim() !== "" &&
      !isInternalId;

    if (hasValidPymidolBooking) {
      console.log("ℹ️ Record already has Pymidol bookingNo:", existingBookingNo);
      setPymidolBookingNo(String(existingBookingNo).trim());
      setIsExistingBooking(true);
      setErrorMsg("");
      setStatus("phase2_success");
      return;
    }

    // Otherwise, bookingNo is NULL -> Start Phase 2 Automation
    setIsExistingBooking(false);
    setStatus("phase2_running");
    setErrorMsg("");

    const targetId = record?.bookingUuid || record?.id;
    console.log("🚀 Triggering Pymidol Phase 2 (Vehicle Booking) for booking record: " + targetId);

    platformApi
      .post("/api/saleRegister/pushVehicleBooking/" + targetId)
      .then((res) => {
        const { data } = res;
        if (
          data &&
          (data.code === 200 || data.code === "200") &&
          !data.error &&
          data.data?.success !== false
        ) {
          const respData = data.data || {};
          const extractedBookingNo =
            respData.bookingNo || data.bookingNo || record?.bookingNo || "";

          setPymidolBookingNo(extractedBookingNo);
          if (respData.customerName) {
            setBookingDetails((prev) => ({
              ...prev,
              customerName: respData.customerName,
            }));
          }

          setStatus("phase2_success");

          if (onSuccess) {
            onSuccess();
          }
        } else {
          setStatus("error");
          const failMsg =
            data?.message ||
            data?.msg ||
            data?.response?.message ||
            "Failed to create Vehicle Booking in Pymidol.";
          setErrorMsg(failMsg);
        }
      })
      .catch((err) => {
        setStatus("error");
        console.error("Pymidol Phase 2 push error:", err);
        const errMsg =
          err?.response?.data?.message ||
          err?.response?.data?.msg ||
          err?.message ||
          "Connection to automation server failed during Phase 2.";
        setErrorMsg(errMsg);
      });
  };

  const checkAndStartPhase3 = () => {
    if (countdownTimerRef.current) {
      clearInterval(countdownTimerRef.current);
      countdownTimerRef.current = null;
    }
    setStatus("phase3_running");
  };

  const handleStartPush = () => {
    if (!record) return;

    const targetId = record.bookingUuid || record.id;
    const custName =
      record.saleData?.customer?.name ||
      record.customerName ||
      record.customer?.name ||
      "N/A";
    const bId = record.bookingId || record.saleId || targetId;

    setBookingDetails({
      customerName: custName,
      bookingId: bId,
    });

    // Check if enquiryNo already exists on this record (Skip Phase 1 automation)
    const existingEnquiryNo = record.enquiryNo || record.saleData?.enquiryNo;
    if (existingEnquiryNo && String(existingEnquiryNo).trim() !== "") {
      console.log("ℹ️ Record already has enquiryNo:", existingEnquiryNo);
      setEnquiryNo(String(existingEnquiryNo).trim());
      setIsExistingEnquiry(true);
      setErrorMsg("");
      setStatus("success");
      return;
    }

    setIsExistingEnquiry(false);
    setStatus("phase1_running");
    setErrorMsg("");
    setEnquiryNo("");

    console.log("🚀 Triggering Pymidol Push for booking record: " + targetId);

    platformApi
      .post("/api/saleRegister/pushToPymidol/" + targetId)
      .then((res) => {
        const { data } = res;
        if (
          data &&
          (data.code === 200 || data.code === "200") &&
          !data.error &&
          data.data?.success !== false
        ) {
          const respData = data.data || {};
          const extractedEnquiry =
            respData.enquiryNo || data.enquiryNo || record.enquiryNo || "";

          setEnquiryNo(extractedEnquiry);
          if (respData.customerName) {
            setBookingDetails((prev) => ({
              ...prev,
              customerName: respData.customerName,
            }));
          }

          setStatus("success");

          if (onSuccess) {
            onSuccess();
          }
        } else {
          setStatus("error");
          const failMsg =
            data?.message ||
            data?.msg ||
            data?.response?.message ||
            "Failed to push Customer Enquiry to Pymidol.";
          setErrorMsg(failMsg);
        }
      })
      .catch((err) => {
        setStatus("error");
        console.error("Pymidol push error:", err);
        const errMsg =
          err?.response?.data?.message ||
          err?.response?.data?.msg ||
          err?.message ||
          "Connection to automation server failed.";
        setErrorMsg(errMsg);
      });
  };

  const isRunning =
    status === "phase1_running" ||
    status === "phase2_running" ||
    status === "waiting_for_otp" ||
    status === "phase3_running";

  const getStatusContent = () => {
    const loadingIcon = (
      <Icon type="loading" style={{ fontSize: 50, color: "#1890ff" }} spin />
    );
    const customerName = bookingDetails?.customerName || "Customer";
    const bookingId = bookingDetails?.bookingId || "-";

    switch (status) {
      case "phase1_running":
        return (
          <div style={{ textAlign: "center", padding: "35px 15px" }}>
            <Spin indicator={loadingIcon} />
            <h3
              style={{
                marginTop: 25,
                color: "#1890ff",
                fontSize: "22px",
                fontWeight: "600",
              }}
            >
              Phase 1: Save the Customer Enquiry
            </h3>
            <p
              style={{
                color: "#333",
                fontSize: "15px",
                maxWidth: "460px",
                margin: "15px auto 0",
                lineHeight: "1.6",
              }}
            >
              Connecting to Pymidol portal and submitting customer enquiry details for{" "}
              <strong>{customerName}</strong>...
            </p>
            <p style={{ color: "#888", fontSize: "13px", marginTop: 12 }}>
              Please wait while the portal processes and generates the Enquiry Number.
            </p>
          </div>
        );

      case "phase2_running":
        return (
          <div style={{ textAlign: "center", padding: "35px 15px" }}>
            <Spin indicator={loadingIcon} />
            <h3
              style={{
                marginTop: 25,
                color: "#1890ff",
                fontSize: "22px",
                fontWeight: "600",
              }}
            >
              Phase 2 : Create the Vehicle Booking
            </h3>
            <p
              style={{
                color: "#333",
                fontSize: "15px",
                maxWidth: "480px",
                margin: "15px auto 0",
                lineHeight: "1.6",
              }}
            >
              Creating Vehicle Booking in Pymidol against Customer Enquiry{" "}
              <strong
                style={{
                  color: "#1890ff",
                  fontFamily: "monospace",
                  backgroundColor: "#e6f7ff",
                  padding: "1px 6px",
                  borderRadius: "4px",
                  border: "1px solid #91d5ff",
                }}
              >
                {enquiryNo || "-"}
              </strong>{" "}
              for <strong>{customerName}</strong>...
            </p>
            <p style={{ color: "#888", fontSize: "13px", marginTop: 12 }}>
              Please wait while Pymidol processes and confirms the vehicle booking record.
            </p>
          </div>
        );

      case "waiting_for_otp": {
        const mins = Math.floor(otpSecondsLeft / 60);
        const secs = String(otpSecondsLeft % 60).padStart(2, "0");
        const displayMobile =
          otpMobileNo ||
          record?.customerPhone ||
          record?.saleData?.customer?.CustomerPhone?.[0]?.phone ||
          "Customer Mobile";

        return (
          <div style={{ textAlign: "center", padding: "20px 15px" }}>
            <div
              style={{
                width: "56px",
                height: "56px",
                borderRadius: "50%",
                backgroundColor: "#e6f7ff",
                border: "2px solid #91d5ff",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                margin: "0 auto 12px",
                fontSize: "26px",
              }}
            >
              📱
            </div>
            <h3 style={{ color: "#1890ff", fontSize: "22px", fontWeight: "600", marginBottom: "6px" }}>
              Customer OTP Verification
            </h3>
            <p style={{ color: "#555", fontSize: "14px", marginBottom: "4px" }}>
              Enter the 4-digit code sent to customer mobile:
            </p>
            <div
              style={{
                fontSize: "16px",
                fontWeight: "700",
                color: "#1890ff",
                letterSpacing: "1px",
                marginBottom: "20px",
                fontFamily: "monospace",
              }}
            >
              📞 +91 {displayMobile}
            </div>

            {/* 4 Separate Digit Boxes */}
            <div style={{ display: "flex", justifyContent: "center", gap: "14px", marginBottom: "18px" }}>
              {[0, 1, 2, 3].map((index) => (
                <input
                  key={index}
                  ref={(el) => (otpInputsRef.current[index] = el)}
                  type="text"
                  inputMode="numeric"
                  pattern="[0-9]*"
                  maxLength={1}
                  disabled={otpSubmitting || otpSecondsLeft <= 0}
                  value={otpValues[index]}
                  onChange={(e) => handleDigitChange(index, e.target.value)}
                  onKeyDown={(e) => handleDigitKeyDown(index, e)}
                  onPaste={handleOtpPaste}
                  style={{
                    width: "55px",
                    height: "60px",
                    fontSize: "26px",
                    fontWeight: "bold",
                    textAlign: "center",
                    borderRadius: "8px",
                    border: otpError ? "2px solid #ff4d4f" : "2px solid #d9d9d9",
                    backgroundColor: otpSubmitting ? "#f5f5f5" : "#ffffff",
                    outline: "none",
                    boxShadow: otpValues[index] ? "0 0 0 2px rgba(24, 144, 255, 0.2)" : "none",
                    transition: "all 0.2s ease",
                  }}
                />
              ))}
            </div>

            {/* In-flight Submitting Spinner */}
            {otpSubmitting && (
              <div style={{ margin: "10px 0", color: "#1890ff", fontSize: "14px", fontWeight: "500" }}>
                <Spin size="small" style={{ marginRight: "8px" }} /> Verifying OTP with Pymidol...
              </div>
            )}

            {/* Error Message on Wrong OTP */}
            {otpError && !otpSubmitting && (
              <div
                style={{
                  color: "#ff4d4f",
                  fontSize: "13px",
                  fontWeight: "600",
                  backgroundColor: "#fff2f0",
                  border: "1px solid #ffccc7",
                  padding: "6px 14px",
                  borderRadius: "6px",
                  display: "inline-block",
                  marginBottom: "12px",
                }}
              >
                ⚠️ {otpError}
              </div>
            )}

            {/* Session Timer & Resend Link */}
            <div style={{ marginTop: "12px", fontSize: "13px", color: "#888" }}>
              <span style={{ marginRight: "12px" }}>
                ⏱️ Session window: <strong style={{ color: otpSecondsLeft < 30 ? "#ff4d4f" : "#333" }}>{mins}:{secs}</strong>
              </span>
              {canResendOtp ? (
                <a
                  onClick={handleResendOtp}
                  style={{ color: "#1890ff", fontWeight: "600", cursor: "pointer", textDecoration: "underline" }}
                >
                  🔄 Send OTP again
                </a>
              ) : (
                <span style={{ color: "#aaa" }}>
                  (Send again in: {resendCooldown}s)
                </span>
              )}
            </div>

            <div style={{ marginTop: "12px", fontSize: "12px", color: "#999" }}>
              ✨ Automatically verifies as soon as 4 digits are entered
            </div>
          </div>
        );
      }

      case "success":
        return (
          <div
            style={{
              textAlign: "center",
              padding: "20px 15px 15px",
              position: "relative",
            }}
          >
            {/* Top Right Corner Countdown Badge for Phase 2 */}
            <div
              onClick={checkAndStartPhase2}
              title="Click to proceed to Phase 2 immediately"
              style={{
                position: "absolute",
                top: "-5px",
                right: "0px",
                display: "flex",
                alignItems: "center",
                gap: "8px",
                padding: "6px 14px",
                backgroundColor: "#f0f5ff",
                border: "1px solid #adc6ff",
                borderRadius: "20px",
                cursor: "pointer",
                boxShadow: "0 2px 8px rgba(47, 84, 235, 0.12)",
                transition: "all 0.3s ease",
              }}
            >
              <Icon
                type="clock-circle"
                theme="twoTone"
                twoToneColor="#2f54eb"
                style={{ fontSize: "15px" }}
              />
              <span
                style={{
                  fontSize: "12px",
                  color: "#595959",
                  fontWeight: "600",
                  textTransform: "uppercase",
                  letterSpacing: "0.5px",
                }}
              >
                Phase 2 in
              </span>
              <span
                style={{
                  fontSize: "16px",
                  fontWeight: "bold",
                  color: "#2f54eb",
                  fontFamily: "monospace",
                  backgroundColor: "#ffffff",
                  padding: "1px 8px",
                  borderRadius: "12px",
                  border: "1px solid #d6e4ff",
                  minWidth: "26px",
                  textAlign: "center",
                }}
              >
                {countdown}s
              </span>
            </div>

            <Icon
              type="check-circle"
              theme="twoTone"
              twoToneColor="#52c41a"
              style={{ fontSize: 62, marginTop: "10px" }}
            />
            <h3
              style={{
                marginTop: 16,
                color: "#52c41a",
                fontSize: "22px",
                fontWeight: "600",
              }}
            >
              {isExistingEnquiry
                ? "Customer Enquiry Already Generated"
                : "Customer Enquiry Saved Successfully"}
            </h3>
            <p
              style={{
                color: "#555",
                fontSize: "15px",
                maxWidth: "480px",
                margin: "10px auto 20px",
                lineHeight: "1.6",
              }}
            >
              {isExistingEnquiry
                ? "This booking already has an active Customer Enquiry in Pymidol."
                : "Customer Enquiry has been saved in Pymidol portal."}
            </p>

            <div
              style={{
                padding: "16px 22px",
                backgroundColor: "#f6ffed",
                border: "1px solid #b7eb8f",
                borderRadius: "8px",
                display: "inline-block",
                textAlign: "left",
                minWidth: "360px",
                maxWidth: "480px",
                marginBottom: "25px",
                boxShadow: "0 2px 6px rgba(0,0,0,0.03)",
              }}
            >
              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  marginBottom: "8px",
                }}
              >
                <span style={{ color: "#777", fontSize: "13px" }}>
                  Customer Name:
                </span>
                <span
                  style={{
                    fontWeight: "600",
                    color: "#262626",
                    fontSize: "14px",
                  }}
                >
                  {customerName}
                </span>
              </div>
              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  marginBottom: "10px",
                }}
              >
                <span style={{ color: "#777", fontSize: "13px" }}>
                  Booking ID:
                </span>
                <span
                  style={{
                    fontWeight: "600",
                    color: "#262626",
                    fontSize: "14px",
                    fontFamily: "monospace",
                  }}
                >
                  {bookingId}
                </span>
              </div>
              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  borderTop: "1px dashed #b7eb8f",
                  paddingTop: "10px",
                }}
              >
                <span
                  style={{
                    color: "#389e0d",
                    fontSize: "14px",
                    fontWeight: "600",
                  }}
                >
                  Enquiry No:
                </span>
                <span
                  style={{
                    fontWeight: "bold",
                    color: "#1890ff",
                    fontSize: "16px",
                    fontFamily: "monospace",
                    backgroundColor: "#e6f7ff",
                    padding: "2px 8px",
                    borderRadius: "4px",
                    border: "1px solid #91d5ff",
                  }}
                >
                  {enquiryNo || "Generated in Pymidol"}
                </span>
              </div>
            </div>
          </div>
        );

      case "phase2_success":
        return (
          <div
            style={{
              textAlign: "center",
              padding: "20px 15px 15px",
              position: "relative",
            }}
          >
            {/* Top Right Corner Countdown Badge for Phase 3 */}
            <div
              onClick={checkAndStartPhase3}
              title="Click to proceed to Phase 3 immediately"
              style={{
                position: "absolute",
                top: "-5px",
                right: "0px",
                display: "flex",
                alignItems: "center",
                gap: "8px",
                padding: "6px 14px",
                backgroundColor: "#f0f5ff",
                border: "1px solid #adc6ff",
                borderRadius: "20px",
                cursor: "pointer",
                boxShadow: "0 2px 8px rgba(47, 84, 235, 0.12)",
                transition: "all 0.3s ease",
              }}
            >
              <Icon
                type="clock-circle"
                theme="twoTone"
                twoToneColor="#2f54eb"
                style={{ fontSize: "15px" }}
              />
              <span
                style={{
                  fontSize: "12px",
                  color: "#595959",
                  fontWeight: "600",
                  textTransform: "uppercase",
                  letterSpacing: "0.5px",
                }}
              >
                Phase 3 in
              </span>
              <span
                style={{
                  fontSize: "16px",
                  fontWeight: "bold",
                  color: "#2f54eb",
                  fontFamily: "monospace",
                  backgroundColor: "#ffffff",
                  padding: "1px 8px",
                  borderRadius: "12px",
                  border: "1px solid #d6e4ff",
                  minWidth: "26px",
                  textAlign: "center",
                }}
              >
                {countdown}s
              </span>
            </div>

            <Icon
              type="check-circle"
              theme="twoTone"
              twoToneColor="#52c41a"
              style={{ fontSize: 62, marginTop: "10px" }}
            />
            <h3
              style={{
                marginTop: 16,
                color: "#52c41a",
                fontSize: "22px",
                fontWeight: "600",
              }}
            >
              {isExistingBooking
                ? "Vehicle Booking Already Generated"
                : "Vehicle Booking Created Successfully"}
            </h3>
            <p
              style={{
                color: "#555",
                fontSize: "15px",
                maxWidth: "480px",
                margin: "10px auto 20px",
                lineHeight: "1.6",
              }}
            >
              {isExistingBooking
                ? "This booking already has an active Vehicle Booking in Pymidol."
                : "Vehicle Booking has been created and saved in Pymidol portal."}
            </p>

            <div
              style={{
                padding: "16px 22px",
                backgroundColor: "#f6ffed",
                border: "1px solid #b7eb8f",
                borderRadius: "8px",
                display: "inline-block",
                textAlign: "left",
                minWidth: "360px",
                maxWidth: "480px",
                marginBottom: "25px",
                boxShadow: "0 2px 6px rgba(0,0,0,0.03)",
              }}
            >
              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  marginBottom: "8px",
                }}
              >
                <span style={{ color: "#777", fontSize: "13px" }}>
                  Customer Name:
                </span>
                <span
                  style={{
                    fontWeight: "600",
                    color: "#262626",
                    fontSize: "14px",
                  }}
                >
                  {customerName}
                </span>
              </div>
              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  marginBottom: "10px",
                }}
              >
                <span style={{ color: "#777", fontSize: "13px" }}>
                  Booking ID:
                </span>
                <span
                  style={{
                    fontWeight: "600",
                    color: "#262626",
                    fontSize: "14px",
                    fontFamily: "monospace",
                  }}
                >
                  {bookingId}
                </span>
              </div>
              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  marginBottom: "10px",
                }}
              >
                <span style={{ color: "#777", fontSize: "13px" }}>
                  Enquiry No:
                </span>
                <span
                  style={{
                    fontWeight: "600",
                    color: "#595959",
                    fontSize: "14px",
                    fontFamily: "monospace",
                  }}
                >
                  {enquiryNo || "-"}
                </span>
              </div>
              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  borderTop: "1px dashed #b7eb8f",
                  paddingTop: "10px",
                }}
              >
                <span
                  style={{
                    color: "#389e0d",
                    fontSize: "14px",
                    fontWeight: "600",
                  }}
                >
                  Pymidol Booking No:
                </span>
                <span
                  style={{
                    fontWeight: "bold",
                    color: "#1890ff",
                    fontSize: "16px",
                    fontFamily: "monospace",
                    backgroundColor: "#e6f7ff",
                    padding: "2px 8px",
                    borderRadius: "4px",
                    border: "1px solid #91d5ff",
                  }}
                >
                  {pymidolBookingNo || "Generated in Pymidol"}
                </span>
              </div>
            </div>
          </div>
        );

      case "phase3_running":
        return (
          <div style={{ textAlign: "center", padding: "35px 15px" }}>
            <Icon
              type="hourglass"
              theme="twoTone"
              twoToneColor="#1890ff"
              style={{ fontSize: 56 }}
            />
            <h3
              style={{
                marginTop: 22,
                color: "#1890ff",
                fontSize: "22px",
                fontWeight: "600",
              }}
            >
              Phase 3 : Next Workflow
            </h3>
            <p
              style={{
                color: "#555",
                fontSize: "15px",
                maxWidth: "480px",
                margin: "12px auto 20px",
                lineHeight: "1.6",
              }}
            >
              Customer Enquiry and Vehicle Booking have been verified.
              <br />
              Phase 3 automation will be initiated here.
            </p>
            <div style={{ display: "flex", justifyContent: "center", marginTop: "20px" }}>
              <Button
                type="primary"
                size="large"
                style={{
                  height: "40px",
                  padding: "0 28px",
                  fontSize: "14px",
                  fontWeight: "600",
                  borderRadius: "5px",
                }}
                onClick={onClose}
              >
                Close
              </Button>
            </div>
          </div>
        );

      case "error":
        return (
          <div style={{ textAlign: "center", padding: "25px 15px" }}>
            <Icon
              type="close-circle"
              theme="twoTone"
              twoToneColor="#f5222d"
              style={{ fontSize: 60 }}
            />
            <h3
              style={{
                marginTop: 18,
                color: "#f5222d",
                fontSize: "22px",
                fontWeight: "600",
              }}
            >
              Automation Failed
            </h3>
            <div
              style={{
                margin: "15px auto 22px",
                padding: "16px 20px",
                backgroundColor: "#ffffff",
                border: "1px solid #d9d9d9",
                borderRadius: "6px",
                textAlign: "left",
                maxWidth: "480px",
                boxShadow: "0 2px 8px rgba(0, 0, 0, 0.05)",
              }}
            >
              <p
                style={{
                  color: "#262626",
                  fontSize: "14px",
                  margin: 0,
                  lineHeight: "1.6",
                  whiteSpace: "pre-line",
                }}
              >
                {errorMsg}
              </p>
            </div>
            <div style={{ display: "flex", justifyContent: "center", gap: "12px" }}>
              <Button
                type="primary"
                onClick={handleStartPush}
                style={{ height: "38px", padding: "0 22px", borderRadius: "5px" }}
              >
                Retry
              </Button>
              <Button
                onClick={onClose}
                style={{ height: "38px", padding: "0 20px", borderRadius: "5px" }}
              >
                Close
              </Button>
            </div>
          </div>
        );

      default:
        return null;
    }
  };

  const canClose = status === "phase3_running" || status === "error";

  return (
    <Modal
      title={
        <span style={{ fontSize: "17px", fontWeight: "600" }}>
          <Icon
            type="sync"
            spin={isRunning}
            style={{ marginRight: 8, color: "#1890ff" }}
          />
          Pymidol Integration — Booking (RFR)
        </span>
      }
      visible={visible}
      onCancel={canClose ? onClose : null}
      footer={null}
      closable={canClose}
      maskClosable={false}
      destroyOnClose
      centered
      width={560}
    >
      {getStatusContent()}
    </Modal>
  );
};

export default PymidolBookingSyncModal;
